import { initClients } from '@/api';
import ConfigLoadError from '@/components/ConfigLoadError';
import { Loadable } from '@/components/Loadable';
import { getCards } from '@/features/cards/cardsActions';
import {
  getConsents,
  getSalesPointConfiguration,
  getSalesPointPaymentTypes,
  setTenant,
} from '@/features/configuration/configurationActions';
import { getProfile } from '@/features/profile/profileActions';
import { useDispatch } from '@/store/utils';
import { getInitialTenant } from '@/utils/tenant';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

interface AppLoaderProps {
  children: ReactNode;
}

const AppLoader = ({ children }: AppLoaderProps) => {
  const dispatch = useDispatch();
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasConfigError, setHasConfigError] = useState(false);

  const initialize = useCallback(async () => {
    setHasConfigError(false);
    setIsInitialized(false);

    const tenant = getInitialTenant();

    await initClients();

    try {
      const salesPointConfiguration = await dispatch(
        getSalesPointConfiguration()
      ).unwrap();
      if (!salesPointConfiguration.id) {
        throw new Error('Sales-point configuration has no ID');
      }
      await dispatch(
        getSalesPointPaymentTypes(salesPointConfiguration.id)
      ).unwrap();
    } catch {
      setHasConfigError(true);
      return;
    }

    await Promise.all([
      dispatch(getCards()),
      dispatch(getConsents()),
      dispatch(getProfile({ clearSessionOnAuthError: true })),
      dispatch(setTenant(tenant)),
    ]);

    setIsInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  if (hasConfigError) {
    return <ConfigLoadError onRetry={initialize} />;
  }

  return (
    <Loadable loading={!isInitialized} className={{ regular: 'h-screen' }}>
      {children}
    </Loadable>
  );
};

export default AppLoader;
